/**
 * Quotes — the FULL quote module (MOCKUP_4 / 5 / 6 · 2026-06-16).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  Owner ruled (2026-06-16) for the full quote module + a VISIBLE Quote entry.
 *  This screen is that surface:
 *   • LIST (MOCKUP_6 frame 1) — the RECORD of quotes: stat strip (₹/$ separate)
 *     + status filter + search → click a row to open its detail.
 *   • DETAIL — the quote read-back + actions: edit lines, Send, Accept
 *     (→ agreed price), Record payment.
 *   • BUILDER (MOCKUP_4) — the detailed multi-line quote: line items (service
 *     from the rate card + qty + rate + amount), add/remove, discount, GST
 *     (currency-driven), firm routing, grand total.
 *   • SEND (MOCKUP_5) — WhatsApp-first message + email co-primary; email works
 *     NOW (Brevo), WhatsApp GATED behind the owner's BSP template approval.
 *
 *  THE ONE SPINE: accepting a quote routes its grand total through
 *  `acceptQuote → setProjectAgreedPrice` (the agreed-price plumbing), which
 *  seeds the money gate + change history. No parallel money path. Recording a
 *  payment reuses the SAME `RecordPaymentFlow` the Payments tab uses.
 *
 *  UI = the ONE locked AVBP system: pure white, slate #2f5d8a accent, no serif/
 *  terracotta, text sidebar (the shell). Admin + staff only (route-gated).
 * ─────────────────────────────────────────────────────────────────────────
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useToast } from '../components/Toast';
import { visibilityFor } from '../lib/roles';
import { Box, Btn3, Card, EmptyNote, Pill, Tag } from '../components/ui';
import RecordPaymentFlow from '../components/RecordPaymentFlow';
import {
  listenClients,
  listenProjects,
  projectLabel,
  type LiveClient,
  type LiveProject,
} from '../lib/projectStore';
import {
  formatINR,
  formatUSD,
  listenServices,
  mergeCatalogue,
  type ServiceCatalogEntry,
} from '../lib/rateCard';
import { listenLeads } from '../lib/leadStore';
import type { Lead } from '../lib/leads';
import type { Currency } from '../lib/firms';
import {
  QUOTE_STATUS_LABEL,
  blankLine,
  canHardDeleteQuote,
  filterQuotes,
  headlineService,
  lineAmount,
  lineFromService,
  milestonesFromService,
  newQuote,
  quoteAdjustments,
  quoteCreditTotal,
  quoteFirmShort,
  quoteMoney,
  quoteRevision,
  quoteTotals,
  reconcileQuote,
  resolveMilestoneAmounts,
  resolveQuoteContactId,
  searchQuotes,
  sortQuotes,
  type Quote,
  type QuoteLine,
  type QuoteMilestone,
  type QuoteStatus,
} from '../lib/quoteModel';
import {
  acceptQuote,
  createQuote,
  deleteQuote,
  listenQuotes,
  resolveQuoteRecipientContact,
  saveQuote,
  sendQuote,
  supersedeAndReviseQuote,
  type AcceptanceReason,
} from '../lib/quoteStore';
import {
  composeQuoteMessagePreview,
  interpretSend,
  payNowComposed,
  quoteServiceText,
  sendChannels,
  whatsappGated,
  type SendChannel,
} from '../lib/quoteSend';
import {
  composeQuoteServiceLine,
  normalizeUseType,
  USE_TYPE_OPTIONS,
  type UseType,
} from '../lib/engagementPhrase';

const SLATE = '#2f5d8a';
const nowISO = () => new Date().toISOString();

/**
 * Contact spine (AD-039) — resolve a quote's single contact anchor for the UI.
 * Uses the PURE `resolveQuoteContactId` precedence (quote.clientId → project
 * .clientId → lead.contactId) against the already-streamed lists (no extra
 * read), then looks the resolved id up in `clients` for the display
 * Name · C#####. Falls back to the lead/quote display name when the contact
 * doc isn't streamed yet (e.g. a lead-only quote with no client master).
 * Returns null when there is no resolvable contact at all.
 */
export function resolveQuoteAnchor(
  quote: Pick<Quote, 'clientId' | 'projectId' | 'leadId' | 'clientName'>,
  ctx: { clients: LiveClient[]; projects: LiveProject[]; leads: Lead[] },
): { name: string; clientNo: string | null; cid: string | null } | null {
  const project = quote.projectId
    ? ctx.projects.find((p) => p.pid === quote.projectId) ?? null
    : null;
  const lead = quote.leadId
    ? ctx.leads.find((l) => l.id === quote.leadId) ?? null
    : null;
  const cid = resolveQuoteContactId(quote, {
    projectClientId: project?.clientId ?? null,
    leadContactId: lead?.contactId ?? null,
  });
  if (cid) {
    const c = ctx.clients.find((x) => x.cid === cid || x.clientNo === cid) ?? null;
    if (c) return { name: c.name || quote.clientName || '—', clientNo: c.clientNo, cid: c.cid };
    // resolved id but the client master isn't streamed (yet / trashed) — still
    // anchor by the id so the UI shows the C##### it links to.
    return { name: quote.clientName || lead?.name || '—', clientNo: cid, cid };
  }
  // No contact resolved — anchor by the lead/quote display name only.
  const name = (quote.clientName || lead?.name || '').trim();
  return name ? { name, clientNo: null, cid: null } : null;
}

/** Whether WhatsApp quote sends are unblocked (Meta template approval).
 *  ENABLED 2026-07-03 (owner-approved): Mack verified DIRECTLY against Meta's
 *  Graph API (GET /message_templates on the 7121 WABA) that the quote pair
 *  `av_quote_payment_v3` + `av_quote_manual_v3` are both APPROVED — see the
 *  dated "Meta verification — 2026-07-03" section in
 *  Deliverables/2026-07-03-whatsapp-surfaces-inventory.md. The send callable
 *  ALSO self-gates server-side on its own template-approval check. */
const WHATSAPP_QUOTE_APPROVED = true;

type View =
  | { kind: 'list' }
  | { kind: 'detail'; quoteId: string }
  // COMPOSE (2026-06-16) — an IN-MEMORY draft. Nothing is written to Firestore
  // until the user hits Save / Save & Send, so an abandoned compose leaves NO
  // orphan draft. `sendAfter` requests a hand-off to Send right after saving.
  // `leadCallNotes` (Phase 2c, 2026-07-03) — the discovery-call notes captured
  // on the lead, surfaced read-only in the composer: they are the INPUT the
  // customized quote is built from.
  | { kind: 'compose'; draft: Quote; leadCallNotes?: string | null }
  // EDIT — an already-persisted draft quote (line/discount/deliverable edits).
  | { kind: 'builder'; quoteId: string }
  | { kind: 'send'; quoteId: string };

export default function Quotes() {
  const { role, user } = useAuth();
  const vis = visibilityFor(role);
  const { toast } = useToast();
  const actorName = user?.displayName || user?.email || 'You';
  // Delete-rule UI gate (mirrors the deployed `quotes_allow_delete` rule):
  // admin → draft + non-accepted; staff (= sales claim) → draft only.
  const roleGate = { isAdmin: role === 'admin', isSales: role === 'staff' || role === 'admin' };

  const [quotes, setQuotes] = useState<Quote[] | null>(null);
  const [projects, setProjects] = useState<LiveProject[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [clients, setClients] = useState<LiveClient[]>([]);
  const [view, setView] = useState<View>({ kind: 'list' });
  const [filter, setFilter] = useState<QuoteStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [recordPid, setRecordPid] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const handledNavLead = useRef(false);

  useEffect(() => {
    const unsub = listenQuotes(
      (rows) => setQuotes(rows),
      () => setQuotes([]),
    );
    return unsub;
  }, []);
  useEffect(() => {
    const unsub = listenProjects(
      (rows) => setProjects(rows),
      () => setProjects([]),
    );
    return unsub;
  }, []);
  useEffect(() => {
    const unsub = listenLeads(
      (rows) => setLeads(rows),
      () => setLeads([]),
    );
    return unsub;
  }, []);
  // Contact spine (AD-039) — the live clients stream maps a resolved
  // `clientId` to its display Name · C##### for the quote's single contact
  // anchor (list + detail). Team-gated read; no rules change.
  useEffect(() => {
    const unsub = listenClients(
      (rows) => setClients(rows),
      () => setClients([]),
    );
    return unsub;
  }, []);

  // VISIBLE QUOTE ENTRY (2026-06-16) — when navigated from a Lead's
  // "❝ Create quote" button, open the COMPOSE form pre-filled for that lead.
  // Nothing is written to Firestore yet (compose-first). Fires once, after
  // leads have loaded, then clears the nav state.
  useEffect(() => {
    if (handledNavLead.current) return;
    const navLeadId = (location.state as { newQuoteLeadId?: string } | null)?.newQuoteLeadId;
    if (!navLeadId || leads.length === 0) return;
    const lead = leads.find((l) => l.id === navLeadId);
    if (!lead) return;
    handledNavLead.current = true;
    navigate('.', { replace: true, state: null }); // clear so a refresh doesn't re-open
    composeFromLead(lead);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads, location.state]);

  // STREAMLINE 1c (2026-07-03) — deep-open a specific quote's DETAIL when
  // navigated from the lead record's inline quote list (`openQuoteId` nav
  // state — same fire-once/clear pattern as `newQuoteLeadId` above).
  const handledNavQuote = useRef(false);
  useEffect(() => {
    if (handledNavQuote.current) return;
    const openId = (location.state as { openQuoteId?: string } | null)?.openQuoteId;
    if (!openId || quotes === null) return;
    handledNavQuote.current = true;
    navigate('.', { replace: true, state: null });
    if (quotes.some((q) => q.id === openId)) {
      setView({ kind: 'detail', quoteId: openId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotes, location.state]);

  // BREAK-1 fix (linkage audit 2026-07-03) — the project-side "Raise quote"
  // door: navigated with `newQuoteProjectId`, open the COMPOSE form
  // pre-filled from that project (mirrors the newQuoteLeadId hook above).
  const handledNavProject = useRef(false);
  useEffect(() => {
    if (handledNavProject.current) return;
    const navPid = (location.state as { newQuoteProjectId?: string } | null)?.newQuoteProjectId;
    if (!navPid || projects.length === 0) return;
    const project = projects.find((p) => p.pid === navPid);
    if (!project) return;
    handledNavProject.current = true;
    navigate('.', { replace: true, state: null });
    composeFromProject(project);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, location.state]);

  // The open quote + its detail-view lookups. MEMOISED (2026-07-03 freeze
  // hardening): these ran inline on EVERY render of this screen — four live
  // listeners (quotes/projects/leads/clients) re-render it on every snapshot,
  // and each render re-scanned all four lists. The owner's frozen-tab
  // incident on /quotes (armed accept → main thread locked) could NOT be
  // reproduced on the real QuoteDetail (e2e lock: quoteAcceptArm.e2e.spec.ts
  // proves arming stays responsive), so this is honest HARDENING of the
  // per-render hot path, not a claimed root-cause fix. Hooks live above the
  // early returns (order-stable).
  const current = useMemo(
    () =>
      quotes !== null &&
      (view.kind === 'detail' || view.kind === 'builder' || view.kind === 'send')
        ? quotes.find((q) => q.id === view.quoteId) ?? null
        : null,
    [quotes, view],
  );
  const currentProject = useMemo(
    () => (current?.projectId ? projects.find((p) => p.pid === current.projectId) ?? null : null),
    [current, projects],
  );
  const currentAnchor = useMemo(
    () => (current ? resolveQuoteAnchor(current, { clients, projects, leads }) : null),
    [current, clients, projects, leads],
  );

  // Quotes-module is team-only (same surfaces as Payments / Leads).
  if (!vis.seesReviewLayer && !vis.seesMoneyInternals) {
    return (
      <Box title="Quotes" note="Restricted">
        <EmptyNote title="Not available on this track">
          Quotes are an admin / staff surface.
        </EmptyNote>
      </Box>
    );
  }

  if (quotes === null) {
    return (
      <Box title="Quotes" note="loading…">
        <EmptyNote title="Loading quotes…">Resolving the quote record.</EmptyNote>
      </Box>
    );
  }

  /** Save a DRAFT EDIT (from the builder) — conformant draft-update write. */
  async function persist(next: Quote) {
    try {
      await saveQuote(next, actorName);
    } catch (e) {
      toast(`Save failed: ${(e as Error).message}`, {});
    }
  }

  function openDetail(id: string) {
    setView({ kind: 'detail', quoteId: id });
  }

  /* ── COMPOSE (compose-first 2026-06-16): build an IN-MEMORY draft, open the
   *    compose form. NOTHING is written to Firestore here — the create write
   *    happens only on explicit Save / Save & Send (see onComposeSave). ── */
  function composeFromLead(lead: Lead) {
    const currency: Currency = lead.agreedCurrency === 'USD' ? 'USD' : 'INR';
    const draft = newQuote({
      id: `QT-${Date.now()}`,
      leadId: lead.id,
      projectId: lead.convertedProjectId,
      clientName: lead.name,
      // Delivery party (owner fix 2026-07-03 "no channel delivered"): carry
      // the lead's contact details onto the quote so the send always has a
      // destination — the snapshot `lead` here is the authoritative pure
      // model (the lead doc's TOP-LEVEL phone/email mirrors can be stale,
      // which is exactly what left the email channel empty).
      leadName: lead.name || null,
      leadPhone: lead.phone?.trim() || null,
      leadEmail: lead.email?.trim() || null,
      tier: lead.assignedTier ?? lead.desiredTier ?? 'L1A',
      currency,
      gst: lead.gstRequired === true,
      now: nowISO(),
    });
    // Auto-fetch the property use type from the lead record (overridable on the
    // form). Unrecognised / missing → left undefined (the service line omits it).
    const autoUse = normalizeUseType(lead.propertyType);
    if (autoUse) draft.useType = autoUse;
    // Carry the hand-set agreed price into the seeded line (mirrors the
    // project-origin path's valueINR override). Without this a lead-origin quote
    // opened at the rate-card standard and silently dropped the agreed figure.
    // Fall back to the rate-card seed only when there's no agreed price.
    const hasAgreed =
      typeof lead.agreedValue === 'number' && lead.agreedValue > 0;
    const seeded =
      hasAgreed && draft.lines.length > 0
        ? {
            ...draft,
            lines: [
              { ...draft.lines[0], rate: Math.round(lead.agreedValue as number) },
              ...draft.lines.slice(1),
            ],
          }
        : draft;
    setNewOpen(false);
    // Phase 2c — carry the discovery-call notes into the composer (read-only
    // context: what was discussed is what the custom quote prices).
    setView({ kind: 'compose', draft: seeded, leadCallNotes: lead.callNotes ?? null });
  }

  function composeFromProject(p: LiveProject) {
    const draft = newQuote({
      id: `QT-${Date.now()}`,
      projectId: p.pid,
      // When the project was converted FROM a lead, stamp that originating
      // leadId for lineage. Pre-back-ref projects (no sourceLeadId) stay
      // lead-less and save on the project-origin branch of the live create rule
      // (2026-07-03 deploy: leadId OR projectId) — no rules deploy needed.
      leadId: p.sourceLeadId ?? null,
      clientId: p.clientId,
      clientName: p.clientName,
      tier: p.tierCode,
      currency: p.currency,
      gst: p.gst ?? p.currency !== 'USD',
      now: nowISO(),
    });
    // BREAK-4 fix: a project RE-quote defaults to the CURRENT agreed figure,
    // not the rate-card standard — the engagement's live value (valueINR, in
    // the project's currency units; the same figure the money gate resolves
    // from) overrides the seeded line's rate when set.
    const seeded =
      p.valueINR > 0 && draft.lines.length > 0
        ? {
            ...draft,
            lines: [{ ...draft.lines[0], rate: p.valueINR }, ...draft.lines.slice(1)],
          }
        : draft;
    setNewOpen(false);
    setView({ kind: 'compose', draft: seeded });
  }

  /**
   * Persist a composed (in-memory) draft on explicit Save / Save & Send. THIS
   * is the only place a brand-new quote is written — `createQuote` writes the
   * bare rule-valid draft. On success: go to the quote detail (or straight to
   * Send if `sendAfter`). On a permission failure (e.g. project-origin needs
   * the team claim is missing) surface it; nothing orphan is left because the
   * write either fully succeeds or never lands a doc.
   */
  async function onComposeSave(draft: Quote, sendAfter: boolean): Promise<void> {
    try {
      await createQuote(draft, actorName);
    } catch (e) {
      const msg = (e as Error).message || '';
      // The live ruleset (2026-07-03 deploy) accepts project-origin quotes, so
      // a permission failure here is a genuine block (e.g. missing team claim),
      // not the old "raise it from the lead / rules pending" case.
      toast(
        /permission/i.test(msg) && !draft.leadId
          ? 'You do not have permission to save this quote — check you are signed in as a team member.'
          : `Save failed: ${msg}`,
        {},
      );
      return;
    }
    toast(sendAfter ? 'Quote saved — ready to send.' : 'Quote saved as a draft.', { ok: true });
    setView(sendAfter ? { kind: 'send', quoteId: draft.id } : { kind: 'detail', quoteId: draft.id });
  }

  /** Hard-delete a quote (rule-gated; surfaces a permission-fail clearly). */
  async function onDelete(q: Quote): Promise<void> {
    try {
      await deleteQuote(q.id);
      toast('Quote deleted.', { ok: true });
      setView({ kind: 'list' });
    } catch (e) {
      const msg = (e as Error).message || '';
      // The delete rule is live (2026-07-03 deploy) — a permission failure here
      // means this role/status can't hard-delete (e.g. an accepted quote, or a
      // non-admin), not that a rules update is pending.
      toast(
        /permission/i.test(msg)
          ? 'You do not have permission to delete this quote (accepted quotes cannot be deleted).'
          : `Delete failed: ${msg}`,
        {},
      );
    }
  }

  /**
   * SUPERSEDE-and-REVISE (2026-07-07) — void the old quote AND open an editable
   * copy. Replaces the old archive-only "Supersede (soft)". On success the new
   * draft is auto-opened in the editor (Rohit's choice) so it can be revised and
   * re-sent. Surfaces a permission failure honestly (nothing half-applied — the
   * create runs first, so a blocked void never leaves a dangling back-ref).
   */
  async function onSupersede(q: Quote): Promise<void> {
    try {
      const { newId } = await supersedeAndReviseQuote(q, actorName);
      toast(`Superseded ${q.id} — revised draft opened. Edit and re-send.`, { ok: true });
      setView({ kind: 'builder', quoteId: newId });
    } catch (e) {
      const msg = (e as Error).message || '';
      toast(
        /permission/i.test(msg)
          ? 'You do not have permission to supersede this quote (an accepted quote can only be superseded by an admin).'
          : `Could not supersede the quote: ${msg}`,
        {},
      );
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {view.kind === 'list' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <Btn3 intent="ink" onClick={() => setNewOpen((o) => !o)}>
              {newOpen ? 'Close' : '+ Create quote'}
            </Btn3>
          </div>

          {newOpen && (
            <NewQuotePicker
              leads={leads}
              projects={projects}
              onPickLead={composeFromLead}
              onPickProject={composeFromProject}
              onClose={() => setNewOpen(false)}
            />
          )}

          <QuotesList
            quotes={quotes}
            clients={clients}
            projects={projects}
            leads={leads}
            filter={filter}
            search={search}
            roleGate={roleGate}
            onFilter={setFilter}
            onSearch={setSearch}
            onOpen={openDetail}
            onDelete={onDelete}
          />
        </>
      )}

      {view.kind === 'compose' && (
        <QuoteComposer
          initial={view.draft}
          leadCallNotes={view.leadCallNotes ?? null}
          onSaveDraft={(next) => onComposeSave(next, false)}
          onSaveAndSend={(next) => onComposeSave(next, true)}
          onCancel={() => setView({ kind: 'list' })}
        />
      )}

      {view.kind === 'detail' && current && (
        <QuoteDetail
          quote={current}
          project={currentProject}
          anchor={currentAnchor}
          actorName={actorName}
          roleGate={roleGate}
          onBack={() => setView({ kind: 'list' })}
          onEdit={() => setView({ kind: 'builder', quoteId: current.id })}
          onSend={() => setView({ kind: 'send', quoteId: current.id })}
          onRecord={(pid) => setRecordPid(pid)}
          onAccepted={() => toast('Quote accepted — its total is now the agreed price.', { ok: true })}
          onDelete={() => onDelete(current)}
          onSupersede={() => onSupersede(current)}
        />
      )}

      {view.kind === 'builder' && current && (
        <QuoteBuilder
          quote={current}
          onSave={async (next) => {
            await persist(next);
            setView({ kind: 'detail', quoteId: next.id });
            toast('Quote saved.', { ok: true });
          }}
          onBack={() => setView({ kind: 'detail', quoteId: current.id })}
        />
      )}

      {view.kind === 'send' && current && (
        <QuoteSendScreen
          quote={current}
          lead={leads.find((l) => l.id === current.leadId) ?? null}
          project={currentProject}
          actorName={actorName}
          onBack={() => setView({ kind: 'detail', quoteId: current.id })}
        />
      )}

      {(view.kind === 'detail' || view.kind === 'send') && !current && (
        <Box title="Quote" note="not found">
          <EmptyNote title="Quote not found">
            <Btn3 intent="ghost" size="sm" onClick={() => setView({ kind: 'list' })}>
              ← Back to quotes
            </Btn3>
          </EmptyNote>
        </Box>
      )}

      {recordPid && (
        <RecordPaymentFlow
          projects={projects}
          initialProjectId={recordPid}
          onClose={() => setRecordPid(null)}
        />
      )}
    </div>
  );
}

/* ════════════════════ NEW-quote picker ════════════════════ */

function NewQuotePicker({
  leads,
  projects,
  onPickLead,
  onPickProject,
  onClose,
}: {
  leads: Lead[];
  projects: LiveProject[];
  onPickLead: (l: Lead) => void;
  onPickProject: (p: LiveProject) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  const [tab, setTab] = useState<'lead' | 'project'>('lead');
  const needle = q.trim().toLowerCase();
  const shownLeads = leads
    .filter((l) => !l.trashed && l.state !== 'rejected')
    .filter((l) => !needle || `${l.name} ${l.email} ${l.id}`.toLowerCase().includes(needle))
    .slice(0, 30);
  const shownProjects = projects
    .filter((p) => !p.archived && !p.trashed)
    .filter(
      (p) =>
        !needle ||
        `${projectLabel(p.projectName, p.vp, p.tier)} ${p.clientName} ${p.vp} ${p.clientNo}`
          .toLowerCase()
          .includes(needle),
    )
    .slice(0, 30);

  return (
    <Box title="Create a quote" note="for a lead, or an existing engagement">
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        <Pill on={tab === 'lead'} onClick={() => setTab('lead')}>
          From a lead
        </Pill>
        <Pill on={tab === 'project'} onClick={() => setTab('project')}>
          For a project
        </Pill>
        <span style={{ marginLeft: 'auto' }}>
          <Btn3 intent="ghost" size="sm" onClick={onClose}>
            Done
          </Btn3>
        </span>
      </div>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={tab === 'lead' ? 'Search leads…' : 'Search projects…'}
        style={pickInput}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10, maxHeight: 320, overflowY: 'auto' }}>
        {tab === 'lead'
          ? shownLeads.map((l) => (
              <button key={l.id} type="button" style={pickRow} onClick={() => onPickLead(l)}>
                <b>{l.name}</b>
                <span style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>{l.id}</span>
                {l.assignedTier && <Tag kind="val">{l.assignedTier}</Tag>}
              </button>
            ))
          : shownProjects.map((p) => (
              <button key={p.pid} type="button" style={pickRow} onClick={() => onPickProject(p)}>
                <b>{projectLabel(p.projectName, p.vp, p.tier)}</b>
                <span style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>
                  {p.clientName} · {p.vp}
                </span>
              </button>
            ))}
        {((tab === 'lead' && shownLeads.length === 0) ||
          (tab === 'project' && shownProjects.length === 0)) && (
          <EmptyNote title="Nothing matches">Refine the search.</EmptyNote>
        )}
      </div>
    </Box>
  );
}

/* ════════════════════ LIST (MOCKUP_6 frame 1) ════════════════════ */

function QuotesList({
  quotes,
  clients,
  projects,
  leads,
  filter,
  search,
  roleGate,
  onFilter,
  onSearch,
  onOpen,
  onDelete,
}: {
  quotes: Quote[];
  clients: LiveClient[];
  projects: LiveProject[];
  leads: Lead[];
  filter: QuoteStatus | 'all';
  search: string;
  roleGate: { isAdmin: boolean; isSales: boolean };
  onFilter: (f: QuoteStatus | 'all') => void;
  onSearch: (s: string) => void;
  onOpen: (id: string) => void;
  onDelete: (q: Quote) => void;
}) {
  const shown = useMemo(
    () => sortQuotes(searchQuotes(filterQuotes(quotes, filter), search)),
    [quotes, filter, search],
  );

  // Stat strip — ₹ and $ totals on separate lines (locked: never summed).
  // MEMOISED (2026-07-03 freeze hardening): these full-list quoteTotals
  // aggregates re-ran on every keystroke/snapshot render; keyed on the quotes
  // list alone so typing in search never re-prices the whole book.
  const { quotedINR, quotedUSD } = useMemo(() => {
    const sum = (cur: 'INR' | 'USD') =>
      quotes
        .filter((q) => q.currency === cur)
        .reduce((s, q) => s + quoteTotals(q.lines, q.currency, q.gst, q.discount, quoteAdjustments(q)).grandTotal, 0);
    return { quotedINR: sum('INR'), quotedUSD: sum('USD') };
  }, [quotes]);
  const counts = (s: QuoteStatus) => quotes.filter((q) => q.status === s).length;

  return (
    <Box title="Quotes" note="the record — view, track, edit · one create path (from a lead / project)">
      {/* stat strip */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <Stat label="Quoted (₹)" value={formatINR(quotedINR)} />
        <Stat label="Quoted ($)" value={formatUSD(quotedUSD)} />
        <Stat
          label="By status"
          value={`${counts('sent')} sent · ${counts('accepted')} accepted · ${counts('paid')} paid`}
        />
      </div>

      {/* filter + search */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'center',
          flexWrap: 'wrap',
          marginBottom: 14,
        }}
      >
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {(['all', 'draft', 'sent', 'accepted', 'paid'] as const).map((s) => (
            <Pill key={s} on={filter === s} onClick={() => onFilter(s)}>
              {s === 'all' ? 'All' : QUOTE_STATUS_LABEL[s]}
            </Pill>
          ))}
        </div>
        <input
          type="search"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Search client or service…"
          aria-label="Search quotes"
          style={{ ...pickInput, flex: 1, minWidth: 200, maxWidth: 300, marginLeft: 'auto' }}
        />
      </div>

      {shown.length === 0 ? (
        <EmptyNote title="No quotes in this view">
          {quotes.length === 0
            ? 'No quotes yet — tap “+ Create quote” to raise the first one.'
            : 'Nothing matches the current filter / search.'}
        </EmptyNote>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {shown.map((q) => {
            const totals = quoteTotals(q.lines, q.currency, q.gst, q.discount, quoteAdjustments(q));
            const anchor = resolveQuoteAnchor(q, { clients, projects, leads });
            return (
              <div
                key={q.id}
                className="row-card"
                role="button"
                tabIndex={0}
                data-testid={`quote-row-${q.id}`}
                onClick={() => onOpen(q.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpen(q.id);
                  }
                }}
                style={{
                  cursor: 'pointer',
                  border: '1px solid var(--rule)',
                  borderRadius: 'var(--r-sm)',
                  padding: '14px 16px',
                  background: 'var(--surface)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{ fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
                    data-testid={`quote-anchor-${q.id}`}
                  >
                    <span>{anchor?.name || q.clientName || '—'}</span>
                    {anchor?.clientNo && <ContactNoChip clientNo={anchor.clientNo} />}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                    {quoteServiceText(q)} · {quoteFirmShort(q)}
                  </div>
                </div>
                <span style={{ marginLeft: 'auto', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  {quoteMoney(totals.grandTotal, q.currency)}
                </span>
                <QuoteStatusChip status={q.status} />
                {canHardDeleteQuote(q.status, roleGate) && (
                  <button
                    type="button"
                    aria-label={`Delete quote for ${q.clientName || 'client'}`}
                    title={q.status === 'draft' ? 'Delete this draft' : 'Delete this quote'}
                    data-testid={`quote-delete-${q.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm(
                        q.status === 'draft'
                          ? 'Delete this draft quote? This cannot be undone.'
                          : `Delete this ${QUOTE_STATUS_LABEL[q.status].toLowerCase()} quote? This cannot be undone.`,
                      )) {
                        onDelete(q);
                      }
                    }}
                    style={{
                      border: '1px solid var(--rule)',
                      background: '#fff',
                      color: 'var(--ink-mute)',
                      cursor: 'pointer',
                      fontSize: 16,
                      lineHeight: 1,
                      padding: '6px 9px',
                      borderRadius: 'var(--r-sm)',
                    }}
                  >
                    🗑
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Box>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        flex: 1,
        minWidth: 150,
        border: '1px solid var(--rule)',
        borderRadius: 'var(--r-sm)',
        padding: '14px 16px',
      }}
    >
      <div style={{ fontSize: 11.5, color: 'var(--ink-mute)', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
        {label}
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, marginTop: 6, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </div>
    </div>
  );
}

/** Contact spine (AD-039) — the C##### anchor chip (vibrant accent on white). */
function ContactNoChip({ clientNo }: { clientNo: string }) {
  return (
    <span
      data-testid="contact-no-chip"
      style={{
        display: 'inline-block',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.02em',
        color: SLATE,
        background: 'var(--accent-wash)',
        border: `1px solid ${SLATE}33`,
        borderRadius: 20,
        padding: '1px 8px',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {clientNo}
    </span>
  );
}

function QuoteStatusChip({ status }: { status: QuoteStatus }) {
  const kind = status === 'paid' || status === 'accepted' ? 'val' : status === 'sent' ? 'warn' : 'tag';
  return <Tag kind={kind as 'val' | 'warn' | 'tag'}>{QUOTE_STATUS_LABEL[status]}</Tag>;
}

/* ════════════════════ DETAIL ════════════════════ */

export function QuoteDetail({
  quote,
  project,
  anchor,
  actorName,
  roleGate,
  onBack,
  onEdit,
  onSend,
  onRecord,
  onAccepted,
  onDelete,
  onSupersede,
}: {
  quote: Quote;
  project: LiveProject | null;
  anchor: { name: string; clientNo: string | null; cid: string | null } | null;
  actorName: string;
  roleGate: { isAdmin: boolean; isSales: boolean };
  onBack: () => void;
  onEdit: () => void;
  onSend: () => void;
  onRecord: (pid: string) => void;
  onAccepted: () => void;
  onDelete: () => void;
  /** SUPERSEDE-and-revise: void this quote + open an editable copy (Fix 1). */
  onSupersede: () => void;
}) {
  const { toast } = useToast();
  const [accepting, setAccepting] = useState(false);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [acceptReason, setAcceptReason] = useState<AcceptanceReason>('other');
  const totals = quoteTotals(quote.lines, quote.currency, quote.gst, quote.discount, quoteAdjustments(quote));

  // STREAMLINE 1d (2026-07-03) — the old "Convert the lead first" dead-end is
  // GONE. A lead-origin quote (no projectId) can be accepted directly:
  // `acceptQuote` branches to the accept→convert orchestrator (quoteAccept),
  // which prices the lead from this total, converts it through the EXISTING
  // promote path, seeds the agreed price via the one money spine and stamps
  // the projectId back on the quote. Each outcome surfaces in plain language.
  async function accept() {
    if (quote.status !== 'sent') {
      toast('Send the quote to the client first — a quote is accepted only after it has been sent.', {});
      return;
    }
    setAccepting(true);
    try {
      const prior = {
        currency: (project?.currency ?? null) as Currency | null,
        value: project && project.valueINR > 0 ? project.valueINR : null,
      };
      // roleGate mirrors the deployed rules' isSalesOrAdmin actor set, so a
      // non-sales session is refused BEFORE any write (Vex F1/Option A).
      const res = await acceptQuote(quote, prior, actorName, acceptReason, roleGate);
      if (res.ok) {
        setAcceptOpen(false);
        if (res.warning) {
          // Accepted AND converted, but the lead doc needs a re-save — say so.
          toast(`Quote accepted. One follow-up: ${res.warning}`, {});
        } else {
          onAccepted();
        }
      } else if (res.error === 'not_sent') {
        toast('Send the quote first — accept only works on a sent quote.', {});
      } else if (res.error === 'incomplete') {
        // The lead is missing must-haves — the message lists exactly which.
        toast(res.message ?? 'Complete the lead record (service, contact details, GST answer) before accepting.', {});
      } else if (res.error === 'lead_not_found') {
        toast(res.message ?? 'The lead on this quote no longer exists — link it to a project or raise a fresh quote.', {});
      } else if (res.error === 'convert_failed') {
        toast(res.message ?? 'The lead could not be converted — nothing was changed. Try again.', {});
      } else if (res.error === 'no_project') {
        toast('This quote has no lead and no project — the accepted price has nowhere to land.', {});
      } else {
        toast(res.message ?? 'Could not accept the quote.', {});
      }
    } catch (e) {
      toast(`Accept failed: ${(e as Error).message}`, {});
    } finally {
      setAccepting(false);
    }
  }

  return (
    <Box title={`Quote · ${quote.clientName}`} note={`${composeQuoteServiceLine(quote)} · ${quoteFirmShort(quote)} · ${QUOTE_STATUS_LABEL[quote.status]}`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Btn3 intent="ghost" size="sm" onClick={onBack}>
            ← Back to quotes
          </Btn3>
          <QuoteStatusChip status={quote.status} />
          {/* Rev N — the version of this quote in its supersede-and-revise chain.
              A revision (rev>1) is highlighted; a plain original shows a quiet Rev 1. */}
          <span data-testid="quote-revision">
            <Tag kind={quoteRevision(quote) > 1 ? 'val' : 'tag'}>Rev {quoteRevision(quote)}</Tag>
          </span>
          <Tag kind="tag">{quote.currency}</Tag>
          <span style={{ marginLeft: 'auto', fontWeight: 800, fontSize: 20, fontVariantNumeric: 'tabular-nums' }}>
            {quoteMoney(totals.grandTotal, quote.currency)}
          </span>
        </div>

        {/* SUPERSEDE lineage (2026-07-07) — the revise relationship, shown where
            it exists: "Revised from #<old>" on the new draft (relatedFrom),
            "Replaced by #<new>" on the superseded original (supersededBy). */}
        {(quote.relatedFrom || quote.supersededBy) && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {quote.relatedFrom && (
              <span data-testid="quote-revised-from">
                <Tag kind="tag">Revised from {quote.relatedFrom} · Rev {quoteRevision(quote)}</Tag>
              </span>
            )}
            {quote.supersededBy && (
              <span data-testid="quote-replaced-by">
                <Tag kind="warn">Replaced by {quote.supersededBy}</Tag>
              </span>
            )}
          </div>
        )}

        {/* Contact spine (AD-039) — the quote's single contact anchor
            (Name · C#####), replacing the old lead-or-client ambiguity. */}
        <div
          data-testid="quote-detail-anchor"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            fontSize: 13,
            color: 'var(--ink-soft)',
            border: '1px solid var(--rule)',
            borderRadius: 'var(--r-sm)',
            background: '#fff',
            padding: '10px 14px',
          }}
        >
          <span style={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--ink-mute)' }}>
            Contact
          </span>
          {anchor ? (
            <>
              <b style={{ fontSize: 14, color: 'var(--ink)' }}>{anchor.name}</b>
              {anchor.clientNo && <ContactNoChip clientNo={anchor.clientNo} />}
            </>
          ) : (
            <span style={{ color: 'var(--ink-mute)' }}>
              Not linked to a contact yet — it resolves when the lead converts or a contact matches.
            </span>
          )}
        </div>

        {/* Composed service line — the plain-English engagement statement the
            client sees (tier engagement phrase + qualifier + use type), or the
            manual override when one is set. Falls back to just the serviceName. */}
        <div
          data-testid="quote-detail-service-line"
          style={{
            fontSize: 13.5,
            color: 'var(--ink)',
            border: '1px solid var(--rule)',
            borderRadius: 'var(--r-sm)',
            background: '#fff',
            padding: '10px 14px',
            lineHeight: 1.5,
          }}
        >
          <span style={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--ink-mute)', marginRight: 8 }}>
            Service
          </span>
          {composeQuoteServiceLine(quote)}
        </div>

        <QuoteLinesReadout quote={quote} totals={totals} />

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {/* Edit line items — DRAFT only. A sent/accepted quote can't be edited
              (the deployed rule forbids it); those route to Supersede & revise
              below instead of a dead "Edit" that errors (no-dead-controls). */}
          {quote.status === 'draft' && (
            <Btn3 intent="ghost" onClick={onEdit}>
              Edit line items
            </Btn3>
          )}
          {/* SUPERSEDE & revise — the real edit path for a sent/accepted quote:
              voids this one and opens an editable copy (Fix 1). */}
          {(quote.status === 'sent' || quote.status === 'accepted') &&
            (roleGate.isSales || roleGate.isAdmin) && (
              <Btn3
                intent="ghost"
                data-testid="quote-supersede-revise"
                onClick={() => {
                  if (
                    window.confirm(
                      quote.status === 'accepted'
                        ? 'Supersede this accepted quote and open an editable copy? The original stays on record, marked superseded.'
                        : 'Supersede this quote and open an editable copy to revise and re-send? The original stays on record, marked superseded.',
                    )
                  ) {
                    onSupersede();
                  }
                }}
              >
                Supersede &amp; revise
              </Btn3>
            )}
          {quote.status === 'draft' && (
            <Btn3 intent="ink" onClick={onSend}>
              Send to client
            </Btn3>
          )}
          {quote.status === 'sent' && (
            <>
              <Btn3 intent="ghost" onClick={onSend}>
                Resend
              </Btn3>
              {/* Vex gate F3 — Accept is a Sales/Admin action (matches the
                  deployed rules' isSalesOrAdmin update gate); other roles
                  never see a button the write would deny. */}
              {(roleGate.isSales || roleGate.isAdmin) && (
                <Btn3
                  intent="grn"
                  onClick={() => (acceptOpen ? accept() : setAcceptOpen(true))}
                  disabled={accepting}
                >
                  {accepting ? 'Accepting…' : acceptOpen ? 'Confirm accept' : 'Accept → set as agreed price'}
                </Btn3>
              )}
            </>
          )}
          {quote.projectId && (quote.status === 'accepted' || quote.status === 'sent') && (
            <Btn3 intent="grn" onClick={() => onRecord(quote.projectId as string)}>
              Record payment
            </Btn3>
          )}

          {/* HARD delete — rule-gated (draft = admin/sales · non-accepted = admin).
              Accepted quotes are never hard-deletable (tied to an agreed price). */}
          {canHardDeleteQuote(quote.status, roleGate) && (
            <Btn3
              intent="ghost"
              size="sm"
              onClick={() => {
                if (window.confirm(
                  quote.status === 'draft'
                    ? 'Delete this draft quote permanently? This cannot be undone.'
                    : `Delete this ${QUOTE_STATUS_LABEL[quote.status].toLowerCase()} quote permanently? This cannot be undone.`,
                )) {
                  onDelete();
                }
              }}
            >
              🗑 Delete{quote.status === 'draft' ? ' draft' : ''}
            </Btn3>
          )}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>
          {quote.status === 'accepted'
            ? 'Accepted quotes can’t be deleted — they’re tied to the agreed price. Supersede & revise voids this one and opens an editable copy instead.'
            : quote.status === 'sent'
              ? 'A sent quote can’t be edited in place. Supersede & revise voids it and opens an editable copy to revise and re-send.'
              : 'Delete clears a useless draft.'}
        </div>

        {acceptOpen && quote.status === 'sent' && (roleGate.isSales || roleGate.isAdmin) && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
            <label style={{ color: 'var(--ink-soft)', fontWeight: 600 }} htmlFor="accept-reason">
              How was it accepted?
            </label>
            <select
              id="accept-reason"
              value={acceptReason}
              onChange={(e) => setAcceptReason(e.target.value as AcceptanceReason)}
              aria-label="Acceptance reason"
              style={{ ...pickInput, width: 'auto', minWidth: 200 }}
            >
              <option value="other">Client agreed (standard)</option>
              <option value="pro_bono_family">Pro-bono — family</option>
              <option value="pro_bono_referral">Pro-bono — referral</option>
              <option value="pro_bono_case_study">Pro-bono — case study</option>
              <option value="partial_waiver">Partial waiver</option>
            </select>
          </div>
        )}

        {quote.status === 'draft' && (
          <div style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
            Send the quote to the client first. A quote becomes the agreed price only
            after it has been sent and then accepted.
          </div>
        )}
        {!quote.projectId && (
          <div style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
            This quote is on a lead. Accepting it converts the lead automatically —
            the client + project are created and this total becomes the agreed price
            (it seeds the money gate). Record payment unlocks right after.
          </div>
        )}
        {quote.projectId && (
          <div style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
            Accepting carries this total ({quoteMoney(totals.grandTotal, quote.currency)}) across as the
            agreed price on {project ? `${project.vp}` : 'the engagement'} — it seeds the money gate.
          </div>
        )}
      </div>
    </Box>
  );
}

export function QuoteLinesReadout({ quote, totals }: { quote: Quote; totals: ReturnType<typeof quoteTotals> }) {
  return (
    <Card flat>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {quote.lines.map((l) => (
          <div key={l.id} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ display: 'flex', gap: 10, fontSize: 13.5, alignItems: 'baseline' }}>
              <span style={{ flex: 1, minWidth: 0 }}>{l.desc || '—'}</span>
              <span style={{ color: 'var(--ink-mute)', fontVariantNumeric: 'tabular-nums' }}>
                {l.qty} × {quoteMoney(l.rate, quote.currency)}
              </span>
              <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', width: 96, textAlign: 'right' }}>
                {quoteMoney(lineAmount(l), quote.currency)}
              </span>
            </div>
            {l.deliverables && (
              <div style={{ fontSize: 11.5, color: 'var(--ink-mute)', paddingLeft: 2 }}>
                {l.deliverables}
              </div>
            )}
          </div>
        ))}
        <div style={{ borderTop: '1px solid var(--rule)', marginTop: 4, paddingTop: 8 }}>
          {/* Structured discount breakdown (optional). When the itemised
              components exist, the subtotal reads "Service value" and each
              component gets its own reduction row — so the discount is
              explainable, not a single opaque number. When absent, this renders
              exactly as before: "Subtotal" + a single "Discount" row. Empty
              components never render a "− $0" row (Rohit's clean-empty rule). */}
          {(() => {
            const showBreakdown = totals.referralCredit > 0 || totals.priorServiceAdjustment > 0;
            const refNote = quote.referralNote?.trim();
            const priorNote = quote.priorServiceNote?.trim();
            return (
              <>
                <TotalRow
                  label={showBreakdown ? 'Service value' : 'Subtotal'}
                  value={quoteMoney(totals.subtotal, quote.currency)}
                />
                {totals.discount > 0 && (
                  <TotalRow label="Discount" value={`− ${quoteMoney(totals.discount, quote.currency)}`} tone="good" />
                )}
                {totals.referralCredit > 0 && (
                  <>
                    <TotalRow
                      label="Referral / relationship credit"
                      value={`− ${quoteMoney(totals.referralCredit, quote.currency)}`}
                      tone="good"
                    />
                    {refNote && (
                      <div style={breakdownNoteStyle} data-testid="quote-referral-note">
                        {refNote}
                      </div>
                    )}
                  </>
                )}
                {totals.priorServiceAdjustment > 0 && (
                  <>
                    <TotalRow
                      label="Adjustment for prior service"
                      value={`− ${quoteMoney(totals.priorServiceAdjustment, quote.currency)}`}
                      tone="good"
                    />
                    {priorNote && (
                      <div style={breakdownNoteStyle} data-testid="quote-prior-service-note">
                        {priorNote}
                      </div>
                    )}
                  </>
                )}
              </>
            );
          })()}
          {totals.tax.applies ? (
            <>
              <TotalRow label={`CGST ${totals.tax.ratePct / 2}%`} value={quoteMoney(totals.tax.cgst, quote.currency)} />
              <TotalRow label={`SGST ${totals.tax.ratePct / 2}%`} value={quoteMoney(totals.tax.sgst, quote.currency)} />
            </>
          ) : (
            <div style={{ fontSize: 11.5, color: 'var(--ink-mute)', padding: '4px 0' }}>{totals.tax.exemptReason}</div>
          )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              borderTop: '2px solid var(--rule-strong)',
              marginTop: 6,
              paddingTop: 8,
            }}
          >
            <b style={{ fontSize: 14 }}>Grand total</b>
            <b style={{ fontSize: 22, fontVariantNumeric: 'tabular-nums' }}>
              {quoteMoney(totals.grandTotal, quote.currency)}
            </b>
          </div>
          {/* Setu credit (L1C → L2x) — a system-computed credit line (NOT a
              discount), written by the computeSetuCredit CF. Shows the client
              the reward for starting on L1C, and the NET payable. */}
          {quoteCreditTotal(quote) > 0 && (
            <>
              {(quote.creditsApplied || []).map((c) => (
                <TotalRow
                  key={c.id}
                  label={`${c.label}${c.sourceQuoteNo ? ` · from ${c.sourceQuoteNo}` : ''}`}
                  value={`− ${quoteMoney(c.amount, quote.currency)}`}
                  tone="good"
                />
              ))}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  borderTop: '1px solid var(--rule)',
                  marginTop: 4,
                  paddingTop: 6,
                }}
              >
                <b style={{ fontSize: 13.5 }}>Net payable</b>
                <b style={{ fontSize: 20, fontVariantNumeric: 'tabular-nums', color: 'var(--good)' }}>
                  {quoteMoney(Math.max(0, totals.grandTotal - quoteCreditTotal(quote)), quote.currency)}
                </b>
              </div>
            </>
          )}
        </div>

        {quote.milestones.length > 0 && (
          <div style={{ borderTop: '1px solid var(--rule)', marginTop: 8, paddingTop: 8 }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--ink-soft)', textTransform: 'uppercase', letterSpacing: '0.02em', marginBottom: 6 }}>
              Payment stages
            </div>
            {resolveMilestoneAmounts(quote.milestones, totals.grandTotal).milestones.map((m, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, fontSize: 12.5, padding: '3px 0', alignItems: 'baseline' }}>
                <span style={{ flex: 1 }}>
                  {m.label}
                  {m.deliverables ? <span style={{ color: 'var(--ink-mute)' }}> — {m.deliverables}</span> : null}
                </span>
                <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                  {quoteMoney(m.amt, quote.currency)} · {m.pct}%
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

function TotalRow({ label, value, tone }: { label: string; value: string; tone?: 'good' }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13, color: 'var(--ink-soft)' }}>
      <span>{label}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums', color: tone === 'good' ? 'var(--good)' : 'var(--ink)', fontWeight: 600 }}>
        {value}
      </span>
    </div>
  );
}

/* ════════════════════ COMPOSE (compose-first 2026-06-16) ════════════════════
 *
 *  The owner ruled the create flow must be DELIBERATE: open a compose form →
 *  fill it in → explicit Save (draft) / Save & Send. NOTHING is written to
 *  Firestore until a button is pressed, so an abandoned compose leaves no
 *  orphan draft. `QuoteComposer` is the builder in compose mode.
 */
export function QuoteComposer({
  initial,
  leadCallNotes = null,
  onSaveDraft,
  onSaveAndSend,
  onCancel,
}: {
  initial: Quote;
  /** Phase 2c — the discovery-call notes captured on the lead (read-only
   *  context: they are the input the customized quote prices). */
  leadCallNotes?: string | null;
  onSaveDraft: (next: Quote) => void | Promise<void>;
  onSaveAndSend: (next: Quote) => void | Promise<void>;
  onCancel: () => void;
}) {
  return (
    <>
      {/* Honest context for a PROJECT-origin draft with NO originating lead.
          The project-origin create rule is LIVE (2026-07-03 deploy), so these
          save normally — this note just records the direct-on-project origin. */}
      {initial.projectId && !initial.leadId && (
        <Card flat style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12.5, color: 'var(--ink-soft)', lineHeight: 1.55 }} data-testid="project-origin-note">
            This quote is raised on <b>{initial.projectId}</b> directly (no
            originating lead on record). It saves like any other quote; nothing
            is written until Save succeeds.
          </div>
        </Card>
      )}
      {leadCallNotes && leadCallNotes.trim() && (
        <Card flat style={{ marginBottom: 14 }}>
          <div
            data-testid="composer-call-notes"
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: 'var(--ink-mute)',
              marginBottom: 6,
            }}
          >
            Call notes from discovery
          </div>
          <div
            style={{
              whiteSpace: 'pre-wrap',
              fontSize: 13.5,
              lineHeight: 1.55,
              color: 'var(--ink)',
            }}
          >
            {leadCallNotes.trim()}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginTop: 6 }}>
            What was discussed on the call — build the custom quote from this.
            Edit the notes on the lead record.
          </div>
        </Card>
      )}
      <QuoteBuilder
        quote={initial}
        mode="compose"
        title="New quote"
        note="compose it, then Save the draft or Save & send — nothing is recorded until you do"
        onSaveDraft={onSaveDraft}
        onSaveAndSend={onSaveAndSend}
        onBack={onCancel}
      />
    </>
  );
}

/* ════════════════════ BUILDER (MOCKUP_4) ════════════════════ */

export function QuoteBuilder({
  quote,
  onSave,
  onBack,
  mode = 'edit',
  title,
  note,
  onSaveDraft,
  onSaveAndSend,
}: {
  quote: Quote;
  /** Edit-mode commit (an already-persisted draft). */
  onSave?: (next: Quote) => void | Promise<void>;
  onBack: () => void;
  /** 'edit' = existing persisted draft · 'compose' = brand-new in-memory draft. */
  mode?: 'edit' | 'compose';
  title?: string;
  note?: string;
  /** Compose-mode: explicit Save draft. */
  onSaveDraft?: (next: Quote) => void | Promise<void>;
  /** Compose-mode: Save then hand off to Send. */
  onSaveAndSend?: (next: Quote) => void | Promise<void>;
}) {
  const [lines, setLines] = useState<QuoteLine[]>(
    quote.lines.length ? quote.lines : [blankLine('ln1')],
  );
  const [milestones, setMilestones] = useState<QuoteMilestone[]>(quote.milestones ?? []);
  const [currency, setCurrency] = useState<Currency>(quote.currency);
  const [gst, setGst] = useState<boolean>(quote.gst);
  // Headline tier + service name — tracked as state so picking the headline
  // (first) line updates the composed service line live (bug fix: pickService
  // used to leave quote.tier stale, poisoning the preview + the persisted
  // serviceLine). Seeded from the quote.
  const [tier, setTier] = useState<string>(quote.tier);
  const [serviceName, setServiceName] = useState<string>(quote.serviceName);
  // Structured discount breakdown (optional) — itemises the opaque discount so
  // the quote can explain the price. Empty strings ⇒ no components ⇒ the totals
  // + saved quote stay identical to a plain quote. Each amount carries its own
  // short note.
  const [referralCredit, setReferralCredit] = useState<string>(
    quote.referralCredit ? String(quote.referralCredit) : '',
  );
  const [referralNote, setReferralNote] = useState<string>(quote.referralNote ?? '');
  const [priorAdj, setPriorAdj] = useState<string>(
    quote.priorServiceAdjustment ? String(quote.priorServiceAdjustment) : '',
  );
  const [priorServiceNote, setPriorServiceNote] = useState<string>(quote.priorServiceNote ?? '');
  // Property use type — seeds the composed service line's noun; '' = auto/omit.
  const [useType, setUseType] = useState<UseType | ''>(quote.useType ?? '');
  // Service description — auto-generated but EDITABLE. Empty ⇒ use the auto line;
  // a typed value overrides it (via composeQuoteServiceLine's manual precedence).
  const [serviceDescription, setServiceDescription] = useState<string>(quote.serviceDescription ?? '');
  const [saving, setSaving] = useState(false);
  // Phase 3d (2026-07-03) — the line picker reads the LIVE rate card merged
  // over the static catalogue (live wins by svcId), so a service the admin
  // creates in Settings → Rate Card appears here immediately. Before the live
  // list loads (or on a read error) the static canon keeps the picker working.
  const [liveServices, setLiveServices] = useState<ServiceCatalogEntry[] | null>(null);
  useEffect(() => {
    const unsub = listenServices(setLiveServices, () => setLiveServices(null));
    return unsub;
  }, []);
  const catalogue = useMemo(() => mergeCatalogue(liveServices), [liveServices]);

  // The ONLY discount controls are now the two structured credits (item 3:
  // consolidate). The legacy generic `discount` INPUT is removed; a NEW quote
  // never sets `discount`. We carry the quote's EXISTING legacy discount through
  // untouched (back-compat display for OLD quotes) — never re-entered, never
  // double-counted (it flows once into quoteTotals below and once into build()
  // via the `...quote` spread).
  const legacyDiscount = quote.discount ?? null;
  const adjustments = {
    referralCredit: Number(referralCredit) || 0,
    priorServiceAdjustment: Number(priorAdj) || 0,
  };
  const totals = quoteTotals(lines, currency, currency === 'USD' ? false : gst, legacyDiscount, adjustments);
  // Resolve the stage ₹ amounts live against the grand total: 'amt' stages keep
  // their locked value, 'pct' stages derive, and the auto (last) stage absorbs
  // the balance so the stages sum EXACTLY to the grand total. Drives the editor
  // display + the "stages must total" validation.
  const resolvedMs = useMemo(
    () => resolveMilestoneAmounts(milestones, totals.grandTotal),
    [milestones, totals.grandTotal],
  );
  const msSum = resolvedMs.milestones.reduce((s, m) => s + m.amt, 0);
  // Reconciled when there are no stages, or they sum to the grand total with no
  // over-allocation. Blocks Save & send (draft save is always allowed).
  const msReconciled =
    milestones.length === 0 || (!resolvedMs.overAllocated && msSum === totals.grandTotal);
  // The AUTO-composed service line (no manual override) — shown to the user as
  // the placeholder + a muted preview so the editable field is never a blank
  // mystery box. Recomputes as the picked service (tier/serviceName) + use type
  // change.
  const autoServiceLine = composeQuoteServiceLine({
    serviceName,
    tier,
    useType: useType || undefined,
  });

  function updateLine(id: string, patch: Partial<QuoteLine>) {
    setLines((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  }
  function removeLine(id: string) {
    setLines((ls) => (ls.length > 1 ? ls.filter((l) => l.id !== id) : ls));
  }
  function addLine() {
    setLines((ls) => [...ls, blankLine(`ln${Date.now()}`)]);
  }
  /** True when `id` is the headline (first) line — the one that drives the
   *  quote's tier / serviceName / composed service line. */
  const isHeadlineLine = (id: string) => lines[0]?.id === id;

  function pickService(id: string, svcId: string) {
    const svc = catalogue.find((s) => s.svcId === svcId);
    if (!svc) {
      // custom line — clear the rate-card linkage + deliverables.
      updateLine(id, { svcId: '', desc: '', deliverables: '' });
      return;
    }
    // AUTO-FILL from the rate card: rate + description + the service's
    // deliverables (editable after). The line keeps its qty.
    const seeded = lineFromService(id, svc, currency);
    updateLine(id, {
      svcId: seeded.svcId,
      desc: seeded.desc,
      rate: seeded.rate,
      deliverables: seeded.deliverables ?? '',
    });
    // Bug fix: picking the HEADLINE service must move the quote's tier +
    // serviceName so the composed service line (and the persisted serviceLine)
    // track the selected service instead of going stale.
    if (isHeadlineLine(id)) {
      setTier(svc.tier ?? svc.svcId);
      setServiceName(svc.name);
    }
    // If the picked service is the headline tier AND no milestones have been
    // set yet, auto-fill the default payment stages from the rate card too.
    if (milestones.length === 0) {
      const ms = milestonesFromService(svc);
      if (ms.length > 0) setMilestones(ms);
    }
  }

  function updateMilestone(i: number, patch: Partial<QuoteMilestone>) {
    setMilestones((ms) => ms.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  }
  /**
   * Delete a stage — ANY stage, INCLUDING the last / the balancer (Rohit's ask
   * 2026-07-07). After removing, the balance folds into the FIRST stage: make it
   * the auto-balancer (and demote any other 'auto' so exactly one balancer
   * remains), then `resolveMilestoneAmounts` re-spreads the ₹ so the stages
   * still sum to the grand total. Deleting down to one stage → that lone stage
   * is the balancer = 100% / the full amount. Never leaves a broken schedule.
   */
  function removeMilestone(i: number) {
    setMilestones((ms) => {
      const next = ms.filter((_, idx) => idx !== i);
      if (next.length === 0) return next;
      return next.map((m, idx) =>
        idx === 0
          ? { ...m, mode: 'auto' as const }
          : m.mode === 'auto'
            ? { ...m, mode: 'pct' as const }
            : m,
      );
    });
  }
  function addMilestone() {
    setMilestones((ms) => [...ms, { label: 'Milestone', isAdvance: false, mode: 'pct', pct: 0, amt: 0, dueRule: 'on_milestone' }]);
  }
  function refillMilestonesFromTier() {
    const svc = headlineService(tier);
    setMilestones(milestonesFromService(svc));
  }

  function build(): Quote {
    return reconcileQuote(
      {
        ...quote,
        // corrected headline tier + serviceName so the composed serviceLine
        // (preview, detail view, and all three persist bodies) tracks the
        // CURRENTLY-picked service.
        tier,
        serviceName,
        lines,
        // Resolve stage ₹ amounts against the grand total so the persisted
        // milestones[] carry correct `amt` + `mode` (fixes the backend
        // `milestones[0].amt === 0` advance bug). Auto stage absorbs rounding.
        milestones: resolveMilestoneAmounts(milestones, totals.grandTotal).milestones,
        currency,
        gst: currency === 'USD' ? false : gst,
        // legacy `discount` is carried through untouched via `...quote` (no
        // input any more) — never re-set here, so no double-count.
        // structured breakdown (optional) — 0 / '' clear cleanly and round-trip.
        referralCredit: Number(referralCredit) || 0,
        referralNote: referralNote.trim() || undefined,
        priorServiceAdjustment: Number(priorAdj) || 0,
        priorServiceNote: priorServiceNote.trim() || undefined,
        // composed-service-line: the property use type ('' → auto/omit) + the
        // editable description override ('' → use the auto-composed line).
        useType: useType || undefined,
        serviceDescription: serviceDescription.trim() || undefined,
      },
      nowISO(),
    );
  }

  async function save() {
    setSaving(true);
    try {
      await onSave?.(build());
    } finally {
      setSaving(false);
    }
  }
  async function saveDraft() {
    setSaving(true);
    try {
      await onSaveDraft?.(build());
    } finally {
      setSaving(false);
    }
  }
  async function saveAndSend() {
    setSaving(true);
    try {
      await onSaveAndSend?.(build());
    } finally {
      setSaving(false);
    }
  }

  return (
    <Box
      title={title ?? 'Detailed quote'}
      note={note ?? 'add a line for each item · the total adds up by itself · tax follows the currency'}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Btn3 intent="ghost" size="sm" onClick={onBack}>
          ← Back
        </Btn3>

        {/* currency */}
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={lblStyle}>Currency</label>
            <div style={segStyle}>
              <button type="button" style={{ ...segBtnStyle, ...(currency === 'INR' ? segOnStyle : null) }} onClick={() => setCurrency('INR')}>
                ₹
              </button>
              <button type="button" style={{ ...segBtnStyle, ...(currency === 'USD' ? segOnStyle : null) }} onClick={() => setCurrency('USD')}>
                $
              </button>
            </div>
          </div>
          {currency === 'INR' && (
            <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 8 }}>
              <input type="checkbox" checked={gst} onChange={(e) => setGst(e.target.checked)} />
              GST invoice needed (→ Aayatan Veda)
            </label>
          )}
        </div>

        {/* lines */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {lines.map((l) => (
            <Card key={l.id} flat>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ flex: 2, minWidth: 200 }}>
                  <label style={lblStyle}>Service / item</label>
                  <select value={l.svcId} onChange={(e) => pickService(l.id, e.target.value)} style={inpStyle} aria-label="Line service">
                    <option value="">— custom line —</option>
                    {catalogue.map((s) => (
                      <option key={s.svcId} value={s.svcId}>
                        {s.svcId} · {s.name}
                      </option>
                    ))}
                  </select>
                  <input
                    value={l.desc}
                    onChange={(e) => updateLine(l.id, { desc: e.target.value })}
                    placeholder="Description"
                    aria-label="Line description"
                    style={{ ...inpStyle, marginTop: 6 }}
                  />
                  {/* DELIVERABLES — auto-filled from the rate card when a
                      service is picked; editable per deal. */}
                  <label style={{ ...lblStyle, marginTop: 8 }}>What the client gets (deliverables)</label>
                  <textarea
                    value={l.deliverables ?? ''}
                    onChange={(e) => updateLine(l.id, { deliverables: e.target.value })}
                    placeholder="Auto-filled from the rate card when you pick a service — edit per deal."
                    aria-label="Line deliverables"
                    data-testid={`line-deliverables-${l.id}`}
                    rows={2}
                    style={{ ...inpStyle, marginTop: 4, resize: 'vertical', minHeight: 44 }}
                  />
                </div>
                <div style={{ width: 70 }}>
                  <label style={lblStyle}>Qty</label>
                  <input
                    value={String(l.qty)}
                    onChange={(e) => updateLine(l.id, { qty: Number(e.target.value.replace(/[^\d.]/g, '')) || 0 })}
                    inputMode="decimal"
                    aria-label="Line qty"
                    style={{ ...inpStyle, textAlign: 'center' }}
                  />
                </div>
                <div style={{ width: 120 }}>
                  <label style={lblStyle}>Rate</label>
                  <input
                    value={String(l.rate)}
                    onChange={(e) => updateLine(l.id, { rate: Number(e.target.value.replace(/[^\d.]/g, '')) || 0 })}
                    inputMode="decimal"
                    aria-label="Line rate"
                    style={{ ...inpStyle, textAlign: 'right' }}
                  />
                </div>
                <div style={{ width: 110, textAlign: 'right' }}>
                  <label style={lblStyle}>Amount</label>
                  <div style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', padding: '8px 0' }} data-testid={`line-amount-${l.id}`}>
                    {quoteMoney(lineAmount(l), currency)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeLine(l.id)}
                  aria-label="Remove line"
                  title="Remove line"
                  style={{ border: 'none', background: 'none', color: 'var(--ink-mute)', cursor: 'pointer', fontSize: 18, padding: '4px 8px' }}
                >
                  ×
                </button>
              </div>
            </Card>
          ))}
          <div>
            <Btn3 intent="ghost" size="sm" onClick={addLine}>
              + Add line
            </Btn3>
          </div>
        </div>

        {/* EXPLAIN THE DISCOUNT (optional) — itemise the discount so the sent
            quote shows the client exactly why the price is what it is. Every
            field is optional; leave them blank for a plain quote. Each amount
            carries its own short note. */}
        <Card flat>
          <label style={{ ...lblStyle, marginBottom: 4 }}>Explain the discount (optional)</label>
          <div style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginBottom: 12, lineHeight: 1.5 }}>
            Itemise the discount so the quote can explain the price to the client. Leave blank for a plain quote —
            nothing extra shows.
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div>
                <label style={lblStyle} htmlFor="q-referral-credit">
                  Referral / relationship credit ({currency === 'USD' ? '$' : '₹'}){' '}
                  <span style={optHint}>· optional</span>
                </label>
                <input
                  id="q-referral-credit"
                  value={referralCredit}
                  onChange={(e) => setReferralCredit(e.target.value.replace(/[^\d.]/g, ''))}
                  inputMode="decimal"
                  placeholder="0"
                  aria-label="Referral or relationship credit amount"
                  data-testid="q-referral-credit"
                  style={{ ...inpStyle, textAlign: 'right' }}
                />
              </div>
              <input
                id="q-referral-note"
                value={referralNote}
                onChange={(e) => setReferralNote(e.target.value)}
                maxLength={120}
                placeholder="Note (e.g. referred by Mr. Sharma)"
                aria-label="Referral credit note"
                data-testid="q-referral-note"
                style={inpStyle}
              />
            </div>
            <div style={{ flex: 1, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div>
                <label style={lblStyle} htmlFor="q-prior-adjustment">
                  Adjustment for prior service ({currency === 'USD' ? '$' : '₹'}){' '}
                  <span style={optHint}>· optional</span>
                </label>
                <input
                  id="q-prior-adjustment"
                  value={priorAdj}
                  onChange={(e) => setPriorAdj(e.target.value.replace(/[^\d.]/g, ''))}
                  inputMode="decimal"
                  placeholder="0"
                  aria-label="Prior-service adjustment amount"
                  data-testid="q-prior-adjustment"
                  style={{ ...inpStyle, textAlign: 'right' }}
                />
              </div>
              <input
                id="q-prior-service-note"
                value={priorServiceNote}
                onChange={(e) => setPriorServiceNote(e.target.value)}
                maxLength={120}
                placeholder="Which service (e.g. L1A paid in Mar)"
                aria-label="Prior-service note"
                data-testid="q-prior-service-note"
                style={inpStyle}
              />
            </div>
          </div>
        </Card>

        {/* PROPERTY USE TYPE (optional) — seeds the composed service line's noun.
            Auto-fetched from the lead record; override or clear here. */}
        <Card flat>
          <label style={lblStyle} htmlFor="q-use-type">
            Property use type <span style={optHint}>· optional, for the service line</span>
          </label>
          <select
            id="q-use-type"
            value={useType}
            onChange={(e) => setUseType(e.target.value as UseType | '')}
            aria-label="Property use type"
            data-testid="q-use-type"
            style={{ ...inpStyle, maxWidth: 320 }}
          >
            <option value="">— auto / not specified —</option>
            {USE_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <div style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginTop: 6, lineHeight: 1.5 }}>
            Used to phrase the service line (e.g. “…for your existing Residence”). Left on auto, it uses the
            property on record — or is omitted if none is known.
          </div>

          {/* SERVICE DESCRIPTION — auto-generated (editable). Empty = use the
              auto line (shown as the placeholder + the muted preview below).
              Typing a value overrides it (manual precedence). */}
          <div style={{ marginTop: 14 }}>
            <label style={lblStyle} htmlFor="q-service-description">
              Service description <span style={optHint}>· auto-generated (editable)</span>
            </label>
            <input
              id="q-service-description"
              value={serviceDescription}
              onChange={(e) => setServiceDescription(e.target.value)}
              maxLength={200}
              placeholder={autoServiceLine || 'Service description'}
              aria-label="Service description (auto-generated, editable)"
              data-testid="q-service-description"
              style={inpStyle}
            />
            {autoServiceLine && (
              <div style={{ fontSize: 11.5, color: 'var(--ink-mute)', marginTop: 6, lineHeight: 1.5 }} data-testid="q-service-description-auto">
                {serviceDescription.trim()
                  ? <>Auto line (cleared to use): <span style={{ color: 'var(--ink-soft)' }}>{autoServiceLine}</span></>
                  : <>This is what the client sees: <span style={{ color: 'var(--ink-soft)' }}>{autoServiceLine}</span></>}
              </div>
            )}
          </div>
        </Card>

        {/* totals */}
        <Card flat>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 380, marginLeft: 'auto' }}>
            {(() => {
              const showBreakdown = totals.referralCredit > 0 || totals.priorServiceAdjustment > 0;
              return (
                <>
                  <TotalRow label={showBreakdown ? 'Service value' : 'Subtotal'} value={quoteMoney(totals.subtotal, currency)} />
                  {/* legacy discount is read-only back-compat (OLD quotes only —
                      there is no input any more). */}
                  {totals.discount > 0 && (
                    <TotalRow label="Discount (existing)" value={`− ${quoteMoney(totals.discount, currency)}`} tone="good" />
                  )}
                  {totals.referralCredit > 0 && (
                    <TotalRow label="Referral / relationship credit" value={`− ${quoteMoney(totals.referralCredit, currency)}`} tone="good" />
                  )}
                  {totals.priorServiceAdjustment > 0 && (
                    <TotalRow label="Adjustment for prior service" value={`− ${quoteMoney(totals.priorServiceAdjustment, currency)}`} tone="good" />
                  )}
                </>
              );
            })()}
            {totals.tax.applies ? (
              <>
                <TotalRow label={`CGST ${totals.tax.ratePct / 2}%`} value={quoteMoney(totals.tax.cgst, currency)} />
                <TotalRow label={`SGST ${totals.tax.ratePct / 2}%`} value={quoteMoney(totals.tax.sgst, currency)} />
              </>
            ) : (
              <div style={{ fontSize: 11.5, color: 'var(--ink-mute)', padding: '2px 0' }}>{totals.tax.exemptReason}</div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', borderTop: '2px solid var(--rule-strong)', marginTop: 6, paddingTop: 8 }}>
              <b style={{ fontSize: 14 }}>Grand total</b>
              <b style={{ fontSize: 24, fontVariantNumeric: 'tabular-nums' }} data-testid="builder-grand-total">
                {quoteMoney(totals.grandTotal, currency)}
              </b>
            </div>
          </div>
        </Card>

        {/* PAYMENT STAGES — auto-filled from the rate card's default
            milestones; editable per deal. Carried onto the quote doc. */}
        <Card flat>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <label style={{ ...lblStyle, marginBottom: 0 }}>Payment stages (milestones)</label>
            <span style={{ marginLeft: 'auto' }}>
              <Btn3 intent="ghost" size="sm" onClick={refillMilestonesFromTier}>
                ↻ Reset to rate card
              </Btn3>
            </span>
          </div>
          {milestones.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
              No payment stages set. Pick the headline service (or “Reset to rate card”) to auto-fill them, or add one.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} data-testid="milestones-list">
              {milestones.map((m, i) => {
                const rm = resolvedMs.milestones[i];
                const isAuto = rm?.mode === 'auto';
                // The field the user is actively editing shows their raw input;
                // the derived counterpart shows the resolved value.
                const amtValue = isAuto
                  ? String(rm?.amt ?? 0)
                  : m.mode === 'amt'
                    ? String(m.amt || '')
                    : String(rm?.amt ?? 0);
                const pctValue = isAuto
                  ? String(rm?.pct ?? 0)
                  : m.mode === 'pct'
                    ? String(m.pct || '')
                    : String(rm?.pct ?? 0);
                return (
                  <div key={i} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div style={{ flex: 2, minWidth: 180 }}>
                      <input
                        value={m.label}
                        onChange={(e) => updateMilestone(i, { label: e.target.value })}
                        placeholder="Stage label"
                        aria-label={`Milestone ${i + 1} label`}
                        style={inpStyle}
                      />
                    </div>
                    {/* ₹ / $ amount — typing here LOCKS the stage (mode='amt') and
                        back-computes its %. The auto (balance) row is read-only. */}
                    <div style={{ width: 120 }}>
                      <div style={{ position: 'relative' }}>
                        <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: 'var(--ink-mute)', pointerEvents: 'none' }}>
                          {currency === 'USD' ? 'US$' : '₹'}
                        </span>
                        <input
                          value={amtValue}
                          readOnly={isAuto}
                          onChange={(e) => updateMilestone(i, { mode: 'amt', amt: Number(e.target.value.replace(/[^\d.]/g, '')) || 0 })}
                          inputMode="decimal"
                          aria-label={isAuto ? `Milestone ${i + 1} amount — balance, auto-calculated` : `Milestone ${i + 1} amount`}
                          title={isAuto ? 'Balance — auto-calculated from the other stages' : undefined}
                          style={{ ...inpStyle, textAlign: 'right', paddingLeft: currency === 'USD' ? 34 : 22, ...(isAuto ? { background: 'var(--inset-deep)', color: 'var(--ink-soft)' } : null) }}
                        />
                      </div>
                    </div>
                    {/* % — typing here sets mode='pct' and derives the amount. */}
                    <div style={{ width: 78 }}>
                      <div style={{ position: 'relative' }}>
                        <input
                          value={pctValue}
                          readOnly={isAuto}
                          onChange={(e) => updateMilestone(i, { mode: 'pct', pct: Number(e.target.value.replace(/[^\d.]/g, '')) || 0 })}
                          inputMode="decimal"
                          aria-label={isAuto ? `Milestone ${i + 1} percent — balance, auto-calculated` : `Milestone ${i + 1} percent`}
                          style={{ ...inpStyle, textAlign: 'right', paddingRight: 20, ...(isAuto ? { background: 'var(--inset-deep)', color: 'var(--ink-soft)' } : null) }}
                        />
                        <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: 'var(--ink-mute)', pointerEvents: 'none' }}>%</span>
                      </div>
                    </div>
                    {/* The balancer still shows its "balance" chip (read-only
                        amount/pct), but EVERY stage — including this one and the
                        last — now carries a delete affordance (Rohit 2026-07-07):
                        deleting folds the balance into the first stage. */}
                    {isAuto && (
                      <span
                        title="This stage auto-fills the balance so the stages always total the grand total."
                        style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--good-ink)', background: 'var(--good-wash)', border: '1px solid var(--good)', borderRadius: 999, padding: '4px 9px', marginBottom: 8, whiteSpace: 'nowrap' }}
                      >
                        balance
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => removeMilestone(i)}
                      aria-label="Remove milestone"
                      title="Delete this stage — its balance folds into the first stage"
                      data-testid={`milestone-remove-${i}`}
                      style={{ border: 'none', background: 'none', color: 'var(--ink-mute)', cursor: 'pointer', fontSize: 18, padding: '4px 8px' }}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {milestones.length > 0 && (
            <div
              data-testid="milestones-total"
              id="milestones-total-msg"
              role="status"
              style={{ marginTop: 8, fontSize: 12.5, fontWeight: 600, color: msReconciled ? 'var(--ink-soft)' : 'var(--crit)' }}
            >
              Stages total {quoteMoney(msSum, currency)} of {quoteMoney(totals.grandTotal, currency)}
              {!msReconciled && (
                <span style={{ marginLeft: 6, fontWeight: 500 }}>
                  — {resolvedMs.overAllocated ? 'the fixed amounts exceed the grand total' : 'these must match before you can send'}.
                </span>
              )}
            </div>
          )}
          <div style={{ marginTop: 8 }}>
            <Btn3 intent="ghost" size="sm" onClick={addMilestone}>
              + Add stage
            </Btn3>
          </div>
        </Card>

        {/* Footer — compose-mode = explicit Save draft / Save & Send / Cancel.
            edit-mode = the single Save quote. */}
        {mode === 'compose' ? (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Btn3 intent="ink" onClick={saveDraft} disabled={saving} data-testid="compose-save-draft">
              {saving ? 'Saving…' : 'Save draft'}
            </Btn3>
            <Btn3
              intent="grn"
              onClick={saveAndSend}
              disabled={saving || !msReconciled}
              data-testid="compose-save-send"
              aria-describedby={!msReconciled && milestones.length > 0 ? 'milestones-total-msg' : undefined}
              title={!msReconciled ? 'Payment stages must total the grand total before you can send.' : undefined}
            >
              {saving ? 'Saving…' : 'Save & send to client'}
            </Btn3>
            <Btn3 intent="ghost" onClick={onBack} disabled={saving} data-testid="compose-cancel">
              Cancel
            </Btn3>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 11.5, color: 'var(--ink-mute)', alignSelf: 'center' }}>
              {!msReconciled ? 'Stages must total the grand total to send.' : 'Nothing is recorded until you Save.'}
            </span>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn3 intent="ink" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save quote'}
            </Btn3>
          </div>
        )}
      </div>
    </Box>
  );
}

/* ════════════════════ SEND (MOCKUP_5) ════════════════════ */

export function QuoteSendScreen({
  quote,
  lead,
  project,
  actorName,
  onBack,
}: {
  quote: Quote;
  lead: Lead | null;
  project: LiveProject | null;
  actorName: string;
  onBack: () => void;
}) {
  const { toast } = useToast();
  const totals = quoteTotals(quote.lines, quote.currency, quote.gst, quote.discount, quoteAdjustments(quote));

  // Bug A — resolve the recipient from the Contact (works for project-origin
  // quotes with no lead), the SAME precedence the CF uses. Seed from the lead
  // synchronously so the screen is never blank, then refine from Firestore.
  const [recipient, setRecipient] = useState<{ name: string | null; phone: string | null; email: string | null }>({
    name: quote.clientName || lead?.name || null,
    phone: lead?.phone ?? null,
    email: lead?.email ?? null,
  });
  useEffect(() => {
    let live = true;
    (async () => {
      const r = await resolveQuoteRecipientContact(quote);
      if (live && (r.name || r.phone || r.email)) {
        setRecipient((prev) => ({
          name: r.name || prev.name,
          phone: r.phone || prev.phone,
          email: r.email || prev.email,
        }));
      }
    })();
    return () => { live = false; };
  }, [quote.id, quote.firmId]);

  const email = recipient.email;
  const phone = recipient.phone;
  const channels = useMemo(
    () => sendChannels({ email, phone, whatsappApproved: WHATSAPP_QUOTE_APPROVED }),
    [email, phone],
  );
  const [picked, setPicked] = useState<Record<SendChannel, boolean>>({
    whatsapp: false,
    email: false,
  });
  // Default-select channels once a destination resolves (don't clobber a manual toggle).
  const autoPicked = useRef(false);
  useEffect(() => {
    if (autoPicked.current) return;
    if (phone || email) {
      autoPicked.current = true;
      setPicked({
        whatsapp: !!phone && WHATSAPP_QUOTE_APPROVED,
        email: !!email,
      });
    }
  }, [phone, email]);
  const [sending, setSending] = useState(false);
  const [sentMsg, setSentMsg] = useState<string | null>(null);

  // The composed service line (engagement phrase + qualifier + use type, or a
  // manual override) IS the {{2}} service slot the client sees in the message.
  const serviceText = composeQuoteServiceLine(quote);
  // The preview MIRRORS the real av_quote_v6 template that sends (subtotal +
  // structured credits + price + pay link + PDF), not the old v3 note.
  const payLink = (quote.payPageUrl || quote.paymentLinkUrl || '').trim();
  const message = composeQuoteMessagePreview({
    clientName: recipient.name || quote.clientName,
    serviceLine: serviceText,
    subtotal: totals.subtotal,
    referralCredit: totals.referralCredit,
    priorServiceAdjustment: totals.priorServiceAdjustment,
    grandTotal: totals.grandTotal,
    currency: quote.currency,
    // {{7}} you-pay-now — resolved from the quote's stages ("in full" for a
    // single payment; Stage-1 advance for a staged quote).
    payNowComposed: payNowComposed(quote.milestones, totals.grandTotal, quote.currency),
    paymentLinkUrl: payLink || null,
  });

  async function doSend() {
    const chosen = (Object.keys(picked) as SendChannel[]).filter((c) => picked[c]);
    if (chosen.length === 0) {
      toast('Pick at least one channel (email works now).', {});
      return;
    }
    setSending(true);
    try {
      const resp = await sendQuote(quote, chosen, { email, phone }, actorName);
      const outcome = interpretSend(resp);
      setSentMsg(outcome.message);
      toast(outcome.message, { ok: outcome.ok });
    } catch (e) {
      toast(`Send failed: ${(e as Error).message}`, {});
    } finally {
      setSending(false);
    }
  }

  return (
    <Box title="Send to client" note={`${recipient.name || quote.clientName} · ${serviceText} · agreed ${quoteMoney(totals.grandTotal, quote.currency)}`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Btn3 intent="ghost" size="sm" onClick={onBack}>
          ← Back
        </Btn3>

        {/* the message (the hero) */}
        <div>
          <label style={lblStyle}>The message your client receives</label>
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'inherit',
              fontSize: 13.5,
              background: 'var(--good-wash)',
              border: '1px solid var(--rule)',
              borderRadius: 'var(--r-sm)',
              padding: '14px 16px',
              color: 'var(--ink)',
              margin: 0,
            }}
          >
            {message}
          </pre>
        </div>

        {/* The pay-link line is now shown IN the preview body (with an inline
            placeholder before send), so no separate note is needed here. */}

        {/* channels */}
        <div>
          <label style={lblStyle}>Send it on</label>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {channels.map((c) => (
              <button
                key={c.channel}
                type="button"
                disabled={c.disabled}
                onClick={() => setPicked((p) => ({ ...p, [c.channel]: !p[c.channel] }))}
                data-testid={`send-channel-${c.channel}`}
                style={{
                  flex: 1,
                  minWidth: 220,
                  textAlign: 'left',
                  border: `1.5px solid ${picked[c.channel] && !c.disabled ? SLATE : 'var(--rule-strong)'}`,
                  borderRadius: 'var(--r-sm)',
                  background: picked[c.channel] && !c.disabled ? 'var(--accent-wash)' : '#fff',
                  padding: '12px 14px',
                  cursor: c.disabled ? 'not-allowed' : 'pointer',
                  opacity: c.disabled ? 0.6 : 1,
                  fontFamily: 'inherit',
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 13.5, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {c.channel === 'whatsapp' ? 'WhatsApp' : 'Email'}
                  {picked[c.channel] && !c.disabled && <span style={{ color: SLATE }}>✓</span>}
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink-mute)', marginTop: 4 }}>{c.dest ?? 'no destination'}</div>
                {c.note && (
                  <div style={{ fontSize: 11.5, color: 'var(--warn)', marginTop: 6 }}>{c.note}</div>
                )}
              </button>
            ))}
          </div>
          {whatsappGated(WHATSAPP_QUOTE_APPROVED) && (
            <div style={{ fontSize: 12, color: 'var(--warn)', marginTop: 10, display: 'flex', gap: 8 }}>
              <span>ⓘ</span>
              <span>
                A WhatsApp quote needs a one-time Meta (BSP) template approval. Until the owner approves it,
                WhatsApp is disabled — <b>email works now</b> and is selected as the safety net.
              </span>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <Btn3 intent="ink" onClick={doSend} disabled={sending}>
            {sending ? 'Sending…' : 'Send quote'}
          </Btn3>
          {!project && (
            <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
              Sending records the quote as sent; the client's reply lands in the project chat once converted.
            </span>
          )}
        </div>

        {sentMsg && (
          <div style={{ fontSize: 13, color: 'var(--ink)', borderLeft: `3px solid ${SLATE}`, paddingLeft: 10 }}>
            {sentMsg}
          </div>
        )}
      </div>
    </Box>
  );
}

/* ════════════════════ styles ════════════════════ */

const pickInput: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  borderRadius: 'var(--r-sm)',
  border: '1px solid var(--rule-strong)',
  fontFamily: 'inherit',
  fontSize: 14,
  boxSizing: 'border-box',
};
const pickRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  textAlign: 'left',
  padding: '9px 11px',
  borderRadius: 'var(--r-sm)',
  border: '1px solid var(--rule)',
  background: 'var(--surface)',
  cursor: 'pointer',
  fontSize: 13,
  color: 'var(--ink)',
  fontFamily: 'inherit',
};
const lblStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--ink-soft)',
  marginBottom: 6,
};
const optHint: React.CSSProperties = {
  fontWeight: 400,
  fontSize: 11,
  color: 'var(--ink-mute)',
};
const breakdownNoteStyle: React.CSSProperties = {
  fontSize: 11.5,
  color: 'var(--ink-mute)',
  lineHeight: 1.4,
  margin: '-2px 0 4px',
  paddingLeft: 2,
};
const inpStyle: React.CSSProperties = {
  width: '100%',
  border: '1px solid var(--rule-strong)',
  borderRadius: 'var(--r-sm)',
  padding: '8px 10px',
  fontSize: 13.5,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: '#fff',
  boxSizing: 'border-box',
  fontVariantNumeric: 'tabular-nums',
};
const segStyle: React.CSSProperties = {
  display: 'inline-flex',
  border: '1.5px solid var(--rule-strong)',
  borderRadius: 'var(--r-sm)',
  overflow: 'hidden',
};
const segBtnStyle: React.CSSProperties = {
  padding: '8px 18px',
  fontSize: 16,
  fontWeight: 700,
  background: '#fff',
  border: 'none',
  color: 'var(--ink-mute)',
  cursor: 'pointer',
};
const segOnStyle: React.CSSProperties = { background: SLATE, color: '#fff' };
